import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { SbEditableBlok, sbEditable } from "@storyblok/storyblok-editable";
import { ButtonV2 } from "../../../atoms/ButtonV2/ButtonV2";
import { HeadingV2, HeadingV2Props } from "../../../atoms/HeadingV2/HeadingV2";
import { RetrieveBookingWithMobileFormV2 } from "../RetrieveBookingWithMobileFormV2/RetrieveBookingWithMobileFormV2";
import { RetrieveBookingWithPNRFormV2 } from "../RetrieveBookingWithPNRFormV2/RetrieveBookingWithPNRFormV2";
import ManageBookingPopUpGclass from "../ManageBookingPopUpGclass";
import { useSelector } from "react-redux";
import { useCommonContext } from "context/CommonDataContext";
import { extractResource } from "helper/extractResource";

export interface ManageBookingRetrieveWidgetV2Props {
  module: string;
  resourceData: {};
  blok: SbEditableBlok & {
    bookingRefInputPlaceholder: string;
    customerInfoInputPlaceholder: string;
    departureCalendarInputLabel: string;
    fromDestinationInputPlaceholder: string;
    mobileNumberInputPlaceholder: string;
    mobileViewHeading: HeadingV2Props["blok"][];
    pnrViewHeading: HeadingV2Props["blok"][];
    submitButtonText: string;
    toDestinationInputPlaceholder: string;
  };  
  key: string;
  className: string;
}

export function ManageBookingRetrieveWidgetV2({
  module,
  resourceData,
  blok: {
    bookingRefInputPlaceholder,
    customerInfoInputPlaceholder,
    departureCalendarInputLabel,
    fromDestinationInputPlaceholder,
    mobileNumberInputPlaceholder,
    mobileViewHeading: mobileViewHeadingBlokArray,
    pnrViewHeading: pnrHeadingBlokArray,
    submitButtonText,
    toDestinationInputPlaceholder,
    ...blokData
  },
  key,
  className
}: ManageBookingRetrieveWidgetV2Props) {
  const router = useRouter();
  const [currentView, setCurrentView] = useState<"PNR" | "Mobile">("PNR");
  const [pnrViewHeading] = pnrHeadingBlokArray || [];
  const [mobileViewHeading] = mobileViewHeadingBlokArray || [];
  const [isWidgetUp, setIsWidgetUp] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const widgetRef = useRef(null);
  const { resources } = useCommonContext();
  const codeShareManageBookingMessage = extractResource(resources, "codeShareManageBookingMessage");

  const groupBooking = useSelector((state: any) => state.persistedReducers.groupBooking);

  const handleToggleView = () => {
    setCurrentView(currentView === "PNR" ? "Mobile" : "PNR");
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    const handleWidgetClick = () => {
      const rect = widgetRef.current.getBoundingClientRect();
      const targetTop = rect.top + window.pageYOffset;
      const offsetPercentage = 22; 
      const offset = (window.innerHeight * offsetPercentage) / 100
    
      if (!hasClicked) {
        window.scrollTo({
          top: targetTop - offset,
          behavior: "smooth",
        });
    
        setHasClicked(true);
      }
    };

    const handleScroll = () => {
      if (hasClicked) {
        setIsWidgetUp(false);
        setHasClicked(false);
      }
    };

    if (widgetRef.current) {
      widgetRef.current.addEventListener("click", handleWidgetClick);
    }

    window.addEventListener("scroll", handleScroll);

    return () => {
      if (widgetRef.current) {
        widgetRef.current.removeEventListener("click", handleWidgetClick);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasClicked, isWidgetUp]);

  const isManageBooking =
    JSON.stringify(router.query) ===
    JSON.stringify({
      slug: ["manage", "view-my-trips"]
    });

  return (
    <>
      <div 
        ref={widgetRef}
        className={`p-4 ${className}`} {...sbEditable(blokData)} key={key}>
          {currentView === "PNR" && pnrViewHeading && <HeadingV2 blok={pnrViewHeading} />}
          {currentView === "Mobile" && mobileViewHeading && <HeadingV2 blok={mobileViewHeading} />}
          {currentView === "PNR" && (
            <RetrieveBookingWithPNRFormV2
              resourceData={resourceData}
              pnrFieldPlaceholder={bookingRefInputPlaceholder}
              emailFieldPlaceholder={customerInfoInputPlaceholder}
              submitCTAText={submitButtonText}
              setGClassOpenModal={setOpenModal}
            />
          )}
          {currentView === "Mobile" && (
            <RetrieveBookingWithMobileFormV2
              resourceData={resourceData}
              fromPlaceholder={fromDestinationInputPlaceholder}
              toPlaceholder={toDestinationInputPlaceholder}
              departureDatePlaceholder={departureCalendarInputLabel}
              mobileNumberPlaceholder={mobileNumberInputPlaceholder}
              submitCTAText={submitButtonText}
              setGClassOpenModal={setOpenModal}
            />
          )}

        <div className="flex items-center justify-center md:justify-start gap-2 lg:mt-0">
          <ButtonV2
            additionalButtonClassName="dark:text-primary"
            blok={{
              text: currentView === "PNR" ? "Retrieve without PNR" : "Retrieve using A PNR",
              variant: "text",
              isFullWidthOnMobile: false
            }}
            onClick={handleToggleView}
          />
          {!isManageBooking && module !== "add-on-widget" && (
            <>
              <hr className="w-4 border-gray-300 border-[1px] rotate-90" />
              <Link legacyBehavior href="/manage-booking/view-my-trips" prefetch={false} passHref>
                <ButtonV2
                  additionalButtonClassName="dark:text-primary"
                  blok={{ text: "View Booking", variant: "text", isFullWidthOnMobile: false }}
                />
              </Link>
            </>
          )}
        </div>
      </div>
      {groupBooking && <ManageBookingPopUpGclass modalTitle="Manage Booking" open={openModal} onClose={closeModal} showLastName={ currentView === "Mobile" ? true : false}/>}

      <div className="px-4 sm:px-0 sm:pl-0 sm:w-full">
       <span className="h-20 sm:h-14 pl-4 bg-background-highlight-orangeL flex items-center font-medium text-xs sm:text-base rounded-lg rounded-tr-none sm:rounded-tl-none"
        dangerouslySetInnerHTML={{  __html: codeShareManageBookingMessage }}
       />
      </div>
    </>
  );
}